import { LoadingSpinner } from '@psyomics/ui-components';
import { Navigate } from 'react-router-dom';

import { ReferralInvite } from '../referral/generated/referral';
import { useReferralInviteQuery } from '../referral/hooks/use-referral-invite-query';

import { SurveyBuilder } from './components/survey-builder';
import { ParsedResult, useInviteSurvey } from './hooks/use-invite-survey';
import { useQuestionnaireStartTracking } from './hooks/use-questionnaire-start-tracking';

interface SurveyProps {
  readonly invitePublicId: string;
  readonly reviewMode?: boolean;
}

const checkState = (reviewMode: boolean, invite: ReferralInvite) => {
  const reviewNotAllowed = reviewMode && (invite.status !== 'completed' || !invite.reviewEnabled);
  if (reviewNotAllowed) throw new Error('cannot review');
};

const parseResult = (result?: ParsedResult) => {
  if (!result?.survey?.definition) throw new Error('no survey definition');
  return {
    definition: result.survey.definition,
    responseId: result.responseId,
  };
};

const Survey = ({ invitePublicId, reviewMode = false }: SurveyProps) => {
  const { result, fetching, errorMessage } = useInviteSurvey(invitePublicId);
  const [invite, inviteFetching] = useReferralInviteQuery(invitePublicId);

  useQuestionnaireStartTracking(invitePublicId);

  if (fetching || inviteFetching) return <LoadingSpinner />;
  if (!invite || errorMessage === 'InviteNotFound') return <Navigate to="/" />;
  if (
    !reviewMode &&
    (invite.referralSession.status === 'expired' || invite.referralSession.referral.status === 'cancelled')
  )
    return <Navigate to="/" />;

  const { responseId, definition } = parseResult(result);
  checkState(reviewMode, invite);

  return (
    <SurveyBuilder
      invitePublicId={invitePublicId}
      reviewMode={reviewMode}
      surveyJson={definition}
      surveyResponseId={responseId}
    />
  );
};

export { Survey };
